'use client';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as Select from '@radix-ui/react-select';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';

import SvgCheck from '../../../icons/basic/Check';
import { DirectionsChevronDown } from '../../../icons/directions';
import { sharedInputClasses } from '../../form/shared-input';
import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';

const SelectItem = forwardRef((
  props: { selected: boolean } & Select.SelectItemProps,
  ref: React.Ref<HTMLDivElement> | undefined,
) => {
  return (
    <Select.Item
      {...props}
      ref={ref}
      className={clsx(
        'px-4 py-3 text-dropdown-enabled outline-none hover:cursor-pointer hover:bg-dropdown-hover hover:text-dropdown-hover data-[disabled]:cursor-default data-[disabled]:bg-dropdown-enabled data-[highlighted]:bg-dropdown-hover data-[disabled]:text-dropdown-disabled data-[highlighted]:text-dropdown-hover',
        props.selected && 'bg-dropdown-selected text-dropdown-selected',
      )}
    >
      <Select.ItemText>
        <Typography
          type="span"
          variant="label-regular"
        >
          {props.children}
        </Typography>
      </Select.ItemText>
      <Select.ItemIndicator className="absolute right-4 inline-flex size-5 text-icon-default">
        <SvgCheck />
      </Select.ItemIndicator>
    </Select.Item>
  );
});

SelectItem.displayName = 'SelectItem';

export type Props = {
  placeholder?: string;
  buttonLabel?: string;
  options: Array<{ label: string; value: string; disabled?: boolean }>;
  testid?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  name?: string;
};

export const Dropdown = forwardRef<HTMLButtonElement, Props>(({
  options,
  placeholder,
  buttonLabel,
  onValueChange,
  testid,
  value,
  defaultValue,
  disabled,
  required,
  name,
}, ref) => {
  const {
    field,
    fieldState,
    formItemId,
  } = useFormField();

  const inputClasses = clsx(sharedInputClasses, {
    'border-input-error bg-input-error': fieldState.error,
  });

  const [open, setOpen] = useState(false);
  const isDisabled = field?.disabled || disabled;

  return (
    <Select.Root
      disabled={isDisabled}
      onOpenChange={() => setOpen(!open)}
      onValueChange={field?.onChange || onValueChange}
      value={field?.value || value}
      defaultValue={defaultValue}
      name={field?.name || name}
      required={required}
    >
      <Select.Trigger
        ref={ref}
        id={formItemId}
        className={clsx(
          inputClasses,
          'relative h-14 w-full truncate pr-10 text-left',
          isDisabled
            ? 'border-none bg-input-disabled'
            : 'bg-input-enabled',
          field?.value
            ? 'text-input-activated'
            : isDisabled
              ? 'text-input-disabled'
              : 'text-input-enabled',
        )}
        aria-label={placeholder ?? buttonLabel ?? 'Select an option'}
        data-testid={testid ? `${testid}Trigger` : undefined}
      >
        <Select.Value
          placeholder={(
            <Typography
              type="span"
              variant="label-regular"
              data-testid={testid}
            >
              {placeholder}
            </Typography>
          )}
        />

        <Select.Icon
          className={clsx(
            'absolute right-4 top-0 flex h-14 w-5 items-center transition-transform',
            open && 'rotate-180',
            isDisabled ? 'text-icon-disabled' : 'text-input-enabled-icon',
          )}
        >
          <DirectionsChevronDown className="size-5" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className="z-50 mt-2 w-[var(--radix-select-trigger-width)] overflow-y-auto bg-dropdown-enabled shadow-large"
          position="popper"
        >
          <ScrollArea.Root
            className="max-h-[260px] w-full"
            type="auto"
          >
            <Select.Viewport asChild>
              <ScrollArea.Viewport className="size-full">
                <Select.Group className="bg-dropdown-enabled">
                  {options
                  .filter(({ value }) => value !== '')
                  .map(({
                    label,
                    value,
                    disabled,
                  }, idx) => (
                    <SelectItem
                      key={`${value}-${idx}`}
                      disabled={disabled}
                      selected={value === field?.value}
                      value={value}
                      data-testid={testid ? `${testid}Option-${idx}` : undefined}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </Select.Group>
              </ScrollArea.Viewport>
            </Select.Viewport>
          </ScrollArea.Root>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
});

Dropdown.displayName = 'Dropdown';
